import { useEffect, useState } from "react";

import { Grid, FormControl, Card } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import i18next from "i18next";

import MDBox from "@vendor/components/MDBox";
import MDButton from "@vendor/components/MDButton";
import { MainLayout } from "@shared/ui/MainLayout";
import { useLoading } from "@shared/hooks/useLoading";
import { SelectController, AutocompleteComponent } from "@shared/ui/form";
import { DataGridTable } from "@shared/ui/DataGridTable";

import { ExecutorActions } from "../components/ExecutorActions";
import { PreInvoiceAdmin } from "../components/PreInvoiceAdmin";
import { useRedmineUsers } from "../hooks/useRedmineUsers";
import { ADMIN_ACTION, ADMIN_MODULE } from "../constants";

import type { AdminAction, AdminModule } from "../constants";
import type { AdminActionResult } from "../components/ExecutorActions";
import type { RedmineUser } from "../api/adminApi";

/**
 * Campos realmente registrados en el formulario.
 *
 * OJO: el schema de yup de abajo esta vacio y, al haber resolver, react-hook-form ignora
 * los `rules` por campo. O sea que hoy este formulario NO valida nada. Se deja como esta
 * a proposito — los flujos de admin se tocan lo minimo — y queda para un ticket aparte.
 */
interface AdminFormValues {
  collaborator?: RedmineUser;
  module?: AdminModule;
  action?: AdminAction;
}

// Placeholder mientras carga la lista de colaboradores de Redmine.
const INITIAL_COLLABORATORS = [{ fullName: "Sin Seleccionar", id: 1 }];

const schema = yup.object().shape({}).required();

const modules = [ADMIN_MODULE.PRE_INVOICES, ADMIN_MODULE.INVOICES];

const actionsAll = {
  [ADMIN_MODULE.INVOICES]: [ADMIN_ACTION.GENERATE, ADMIN_ACTION.CONSULT],
  [ADMIN_MODULE.PRE_INVOICES]: [ADMIN_ACTION.GENERATE, ADMIN_ACTION.CONSULT],
};

export const AdminPage = () => {
  const [collaborator, setCollaborator] = useState<RedmineUser | undefined>();
  const [action, setAction] = useState<AdminAction[]>([]);
  const [info, setInfo] = useState<AdminActionResult | null>(null);
  const { loading, thisLoading, notLoading } = useLoading();
  const [clickSubmit, setClickSubmit] = useState(false);

  // Lista de colaboradores vía hook; el placeholder cubre carga y error.
  const { data: collaboratorList = INITIAL_COLLABORATORS, isFetching } = useRedmineUsers();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AdminFormValues>({
    resolver: yupResolver(schema),
  });

  const moduleSelection = watch("module");
  const actionSelection = watch("action");

  const onChangeCollaborator = (value: RedmineUser | null) => {
    setCollaborator(value ?? undefined);
  };

  useEffect(() => {
    Object.entries(actionsAll).forEach(([key, val]) => {
      if (key === moduleSelection) {
        setAction(val);
      }
    });
    setClickSubmit(false);
  }, [moduleSelection]);

  useEffect(() => {
    setClickSubmit(false);
  }, [actionSelection]);

  const onExecute = () => {
    // Guard minimo: sin schema de yup no hay validacion, asi que se puede llegar aca sin
    // colaborador elegido y `collaborator.id` lanzaba. No se toca la validacion en si.
    if (!collaborator || !collaborator.id || !actionSelection || !moduleSelection) return;

    setClickSubmit(false);
    thisLoading();
    // ExecutorActions no retorna en la rama `default` de sus switch (accion no implementada),
    // con lo cual esa ruta hoy lanza TypeError al encadenar .then. Se conserva tal cual: el
    // `!` mantiene el comportamiento actual en vez de taparlo con `?.`, que dejaria el
    // spinner encendido en silencio. Queda anotado para el ticket de validacion del panel.
    ExecutorActions(actionSelection, moduleSelection, thisLoading, notLoading, collaborator.id)!
      .then((value?: AdminActionResult) => {
        setInfo(value ?? null);
        setClickSubmit(true);
      })
      .finally(() => {
        notLoading();
      });
  };

  return (
    <MainLayout loading={loading || isFetching}>
      <MDBox py={3}>
        <Card sx={{ height: "100%" }}>
          <MDBox py={3} px={1}>
            <form onSubmit={handleSubmit(onExecute)}>
              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 6,
                    md: 4,
                    lg: 4,
                    xl: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <Controller
                      name="collaborator"
                      control={control}
                      rules={{ required: true }}
                      render={() => (
                        <AutocompleteComponent
                          onChange={onChangeCollaborator}
                          listValue={collaboratorList as { fullName: string }[]}
                          valueSelected={(collaborator ?? null) as { fullName: string } | null}
                          label={i18next.t("collaborator")}
                          placeholder={i18next.t("collaborator")}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 4,
                    lg: 4,
                    xl: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <SelectController
                      control={control}
                      defaultValue=""
                      label={i18next.t("module")}
                      name="module"
                      listValue={modules}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 4,
                    lg: 4,
                    xl: 3,
                  }}
                >
                  <FormControl fullWidth disabled>
                    <SelectController
                      control={control}
                      defaultValue={[]}
                      label={i18next.t("action")}
                      name="action"
                      listValue={action}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 4,
                    lg: 4,
                    xl: 3,
                  }}
                >
                  <MDButton variant="gradient" type="submit" color="info" fullWidth>
                    {i18next.t("execute")}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
            {actionSelection === ADMIN_ACTION.CONSULT && info !== null && clickSubmit && (
              <>
                <br />
                <DataGridTable
                  rows={info.data}
                  title={info.title}
                  columns={info.columns}
                  language={i18next.language}
                />
              </>
            )}
            {actionSelection === ADMIN_ACTION.GENERATE &&
              moduleSelection === ADMIN_MODULE.PRE_INVOICES &&
              clickSubmit && (
                <PreInvoiceAdmin
                  notLoading={notLoading}
                  thisLoading={thisLoading}
                  userData={collaborator as { id: number }}
                />
              )}
          </MDBox>
        </Card>
      </MDBox>
    </MainLayout>
  );
};
